import { AdminModule } from '../utils/adminPermissions';

export type PaletteGroup =
  | 'Suggested'
  | 'Recent'
  | 'Pages'
  | 'Search data'
  | 'Quick actions';

export type NavItem = {
  id: string;
  label: string;
  path: string;
  hint: string;
  keywords: string[];
  group: PaletteGroup;
  module?: AdminModule;
  searchable?: boolean;
  searchHint?: string;
};

export type QueryIntent = {
  type: 'phone' | 'email' | 'bookingId' | 'text';
  suggestedModuleIds: string[];
};

export const RECENT_SEARCH_KEY = 'nouvak.recentSearches';
export const RECENT_PAGES_KEY = 'nouvak.recentPages';
export const MAX_RECENT = 8;

/** All pages + searchable modules in one registry */
export const PALETTE_ITEMS: NavItem[] = [
  // Main
  { id: 'home', label: 'Dashboard', path: '/home', hint: 'Stats & charts', group: 'Pages', module: 'dashboard', keywords: ['dashboard', 'home', 'analytics', 'stats'] },
  { id: 'enquiries', label: 'Enquiries', path: '/enquires', hint: 'All enquiries', group: 'Pages', module: 'enquiries', searchable: true, searchHint: 'Customer, phone, status', keywords: ['enquiry', 'enquire', 'inquiry'] },
  { id: 'cancelled', label: 'Cancelled Bookings', path: '/cancelled', hint: 'Cancelled list', group: 'Pages', module: 'cancelled', searchable: true, searchHint: 'Booking ID, user', keywords: ['cancel', 'cancelled'] },
  { id: 'payments', label: 'Payments', path: '/payments', hint: 'Payment records', group: 'Pages', module: 'payments', searchable: true, searchHint: 'Booking ID, amount', keywords: ['payment', 'paid', 'pending', 'invoice'] },
  { id: 'users', label: 'Users / Customers', path: '/customers', hint: 'Customer directory', group: 'Pages', module: 'customers', searchable: true, searchHint: 'Name, phone, email', keywords: ['user', 'customer', 'client', 'member'] },
  { id: 'bookings', label: 'Bookings', path: '/bookings', hint: 'All bookings', group: 'Pages', module: 'bookings', searchable: true, searchHint: 'Booking ID, name, phone', keywords: ['booking', 'order', 'appointment', 'schedule'] },
  { id: 'promotions', label: 'Promotions', path: '/promotions', hint: 'Notifications & promos', group: 'Pages', module: 'promotions', keywords: ['promotion', 'notification', 'push'] },
  { id: 'leads', label: 'Leads Management', path: '/leads', hint: 'Sales leads', group: 'Pages', module: 'leads', searchable: true, searchHint: 'Lead name, contact', keywords: ['lead', 'prospect', 'sales'] },
  { id: 'channel-partners', label: 'Channel Partners', path: '/channel-partners', hint: 'Partner onboarding applications', group: 'Pages', module: 'partner_onboarding', searchable: true, searchHint: 'Name, phone, email', keywords: ['channel partner', 'partner', 'referral', 'onboarding'] },
  { id: 'partner-leads', label: 'Partner Leads', path: '/channel-partners/leads', hint: 'Customer jobs submitted by partners', group: 'Pages', module: 'partnerLeads', searchable: true, searchHint: 'Lead ID, customer, phone', keywords: ['partner lead', 'channel partner lead', 'referral lead'] },
  { id: 'channel-partners-identity', label: 'Partner Identity Review', path: '/channel-partners/identity', hint: 'Manual identity checks', group: 'Pages', module: 'partner_onboarding', keywords: ['identity', 'review', 'kyc', 'partner documents'] },

  // Technicians
  { id: 'tech-all', label: 'All Technicians', path: '/technicians', hint: 'Technician roster', group: 'Pages', module: 'technicians', searchable: true, searchHint: 'Name, phone, ID', keywords: ['technician', 'tech', 'engineer'] },
  { id: 'tech-attendance', label: 'Technician Attendance', path: '/technicians/attendance', hint: 'Daily attendance', group: 'Pages', module: 'attendance', searchable: true, searchHint: 'Technician name', keywords: ['attendance', 'present', 'absent'] },
  { id: 'tech-newjoin', label: 'New Join Technicians', path: '/technicians/new-join', hint: 'Recently joined', group: 'Pages', module: 'technicians', keywords: ['new join', 'onboarding'] },
  { id: 'tech-acd', label: 'NVK Technicians', path: '/technicians/acd-technicians', hint: 'NVK assigned techs', group: 'Pages', module: 'technicians', searchable: true, searchHint: 'Technician name', keywords: ['nvk', 'acd', 'technician'] },
  { id: 'tech-helper', label: 'NVK Helpers', path: '/technicians/acd-helper', hint: 'Helper staff', group: 'Pages', module: 'helpers', searchable: true, searchHint: 'Helper name', keywords: ['helper', 'nvk helper', 'acd helper'] },
  { id: 'tech-contractor', label: 'Contractors', path: '/technicians/contractor', hint: 'Contractor list', group: 'Pages', module: 'technicians', searchable: true, searchHint: 'Contractor name', keywords: ['contractor'] },
  { id: 'tech-tool-req', label: 'Tool Requests', path: '/technicians/tool-request', hint: 'Technician tool requests', group: 'Pages', module: 'tools', searchable: true, searchHint: 'Request ID, tech', keywords: ['tool request', 'request tool'] },
  { id: 'leaves', label: 'Leaves', path: '/manage/leaves', hint: 'Leave management', group: 'Pages', module: 'technicians', keywords: ['leave', 'holiday', 'off'] },

  // Manage
  { id: 'services', label: 'Services', path: '/manage/services', hint: 'Service catalog', group: 'Pages', module: 'services', searchable: true, searchHint: 'Service name', keywords: ['service', 'repair', 'ac service'] },
  { id: 'coupons', label: 'Coupons', path: '/manage/coupons', hint: 'Discount coupons', group: 'Pages', module: 'coupons', searchable: true, searchHint: 'Coupon code', keywords: ['coupon', 'discount', 'promo code'] },
  { id: 'banners', label: 'Banners', path: '/manage/banners', hint: 'App banners', group: 'Pages', module: 'banners', keywords: ['banner', 'slider', 'hero'] },
  { id: 'products', label: 'Products', path: '/Product', hint: 'Product catalog', group: 'Pages', module: 'products', searchable: true, searchHint: 'Product name, SKU', keywords: ['product', 'sku', 'item', 'spare'] },
  { id: 'material', label: 'Material', path: '/manage/material', hint: 'Material inventory', group: 'Pages', module: 'material', searchable: true, searchHint: 'Material name', keywords: ['material', 'inventory', 'stock'] },
  { id: 'taxation', label: 'Taxation', path: '/manage/taxation', hint: 'Tax settings', group: 'Pages', module: 'taxation', searchable: true, searchHint: 'Tax name', keywords: ['tax', 'gst', 'taxation'] },
  { id: 'tools-manage', label: 'Tools (Manage)', path: '/manage/tools', hint: 'Tool bags & inventory', group: 'Pages', module: 'tools', searchable: true, searchHint: 'Tool name', keywords: ['tool', 'toolbag', 'equipment'] },

  // Requests
  { id: 'amc', label: 'AMC Requests', path: '/request/amc', hint: 'Annual maintenance', group: 'Pages', module: 'amc', searchable: true, searchHint: 'Customer, AMC ID', keywords: ['amc', 'maintenance', 'annual'] },
  { id: 'copper', label: 'Copper Piping', path: '/request/copperpiping', hint: 'Copper piping requests', group: 'Pages', module: 'copper_piping', searchable: true, searchHint: 'Customer, request', keywords: ['copper', 'piping', 'pipe'] },
  { id: 'consultancy', label: 'Free Consultancy', path: '/request/free-consultancy', hint: 'Consultancy requests', group: 'Pages', module: 'consultancy', searchable: true, searchHint: 'Customer name', keywords: ['consultancy', 'consult', 'free'] },
  { id: 'purchase', label: 'Purchase Leads', path: '/purchase', hint: 'Shop purchase leads', group: 'Pages', module: 'purchase', searchable: true, searchHint: 'Lead, product', keywords: ['purchase', 'shop', 'buy'] },
  { id: 'sell-ac', label: 'Sell Old AC', path: '/request/sell-old-ac', hint: 'Old AC sell requests', group: 'Pages', module: 'sell_old_ac', searchable: true, searchHint: 'Customer, AC details', keywords: ['sell', 'old ac', 'scrap'] },

  // Utility
  { id: 'error-codes', label: 'Error Codes', path: '/utility/error-codes', hint: 'AC error codes', group: 'Pages', module: 'utility', searchable: true, searchHint: 'Error code, brand', keywords: ['error', 'code', 'fault'] },
  { id: 'tonnage', label: 'Tonnage Calculator', path: '/utility/tonnage-calculator', hint: 'AC tonnage tool', group: 'Pages', module: 'utility', keywords: ['tonnage', 'calculator', 'btu'] },
  { id: 'comparison', label: 'AC Comparison', path: '/utility/comparison', hint: 'Compare AC models', group: 'Pages', module: 'utility', keywords: ['compare', 'comparison', 'model'] },
  { id: 'admin-staff', label: 'Admin Roles', path: '/admin-staff', hint: 'Admin roles & permissions', group: 'Pages', module: 'admin_staff', searchable: true, searchHint: 'Admin name, email', keywords: ['admin', 'staff', 'role', 'permissions'] },

  // Quick actions
  { id: 'create-booking', label: 'Create New Booking', path: '/bookings/create', hint: 'Add a booking', group: 'Quick actions', module: 'bookings', keywords: ['create booking', 'new booking', 'add booking'] },
  { id: 'add-product', label: 'Add Product', path: '/add-product', hint: 'Create product', group: 'Quick actions', module: 'products', keywords: ['add product', 'new product'] },
  { id: 'create-service', label: 'Create Service', path: '/manage/services/create', hint: 'Add service', group: 'Quick actions', module: 'services', keywords: ['create service', 'new service'] },
];

export const SEARCHABLE_ITEMS = PALETTE_ITEMS.filter((i) => i.searchable);

export function fuzzyScore(text: string, query: string): number {
  const t = text.toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;

  let ti = 0;
  let score = 0;
  for (let qi = 0; qi < q.length; qi += 1) {
    const idx = t.indexOf(q[qi], ti);
    if (idx === -1) return 0;
    score += 10 - Math.min(idx - ti, 9);
    ti = idx + 1;
  }
  return score;
}

export function scoreItem(item: NavItem, query: string): number {
  const q = query.trim();
  if (!q) return item.group === 'Pages' ? 50 : 40;

  let score = fuzzyScore(item.label, q);
  score = Math.max(score, fuzzyScore(item.hint, q));
  item.keywords.forEach((kw) => {
    score = Math.max(score, fuzzyScore(kw, q));
  });
  return score;
}

export function detectQueryIntent(query: string): QueryIntent {
  const q = query.trim();
  if (/^[\d\s+-]{10,}$/.test(q.replace(/\s/g, ''))) {
    return { type: 'phone', suggestedModuleIds: ['users', 'bookings', 'partner-leads', 'channel-partners', 'tech-all', 'enquiries'] };
  }
  if (q.includes('@')) {
    return { type: 'email', suggestedModuleIds: ['users', 'channel-partners', 'admin-staff'] };
  }
  if (/^(acd|bk|booking)[-_\s]?\d+/i.test(q) || (/^\d{5,}$/.test(q) && q.length <= 12)) {
    return { type: 'bookingId', suggestedModuleIds: ['bookings', 'payments', 'cancelled', 'enquiries'] };
  }
  return { type: 'text', suggestedModuleIds: ['bookings', 'users', 'partner-leads', 'channel-partners', 'enquiries', 'tech-all'] };
}

export function filterPaletteItems(query: string, allowedIds?: Set<string>): NavItem[] {
  const q = query.trim().toLowerCase();
  const items = allowedIds
    ? PALETTE_ITEMS.filter((i) => allowedIds.has(i.id))
    : [...PALETTE_ITEMS];

  if (!q) {
    return items.filter((i) => i.group === 'Pages' || i.group === 'Quick actions');
  }

  return items
    .map((item) => ({ item, score: scoreItem(item, q) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

export function getSearchableForQuery(query: string, allowedIds?: Set<string>): NavItem[] {
  const intent = detectQueryIntent(query);
  const q = query.trim();
  if (!q) return [];

  const searchable = SEARCHABLE_ITEMS.filter((i) => !allowedIds || allowedIds.has(i.id));

  const ranked = searchable
    .map((item) => {
      let score = scoreItem(item, q);
      if (intent.suggestedModuleIds.includes(item.id)) score += 25;
      return { item, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score);

  return ranked.map(({ item }) => item);
}

export function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCH_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function pushRecentSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed || trimmed.length < 2) return;
  const prev = getRecentSearches().filter((s) => s !== trimmed);
  localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify([trimmed, ...prev].slice(0, MAX_RECENT)));
}

export type RecentPage = { id: string; label: string; path: string };

export function getRecentPages(): RecentPage[] {
  try {
    const raw = localStorage.getItem(RECENT_PAGES_KEY);
    return raw ? (JSON.parse(raw) as RecentPage[]) : [];
  } catch {
    return [];
  }
}

export function pushRecentPage(page: RecentPage) {
  const prev = getRecentPages().filter((p) => p.path !== page.path);
  localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify([page, ...prev].slice(0, MAX_RECENT)));
}

/** @deprecated use filterPaletteItems */
export function filterNavItems(query: string): NavItem[] {
  return filterPaletteItems(query);
}

/** @deprecated use SEARCHABLE_ITEMS */
export const QUICK_NAV = PALETTE_ITEMS.filter((i) => i.group === 'Pages');
export const SEARCH_MODULES = SEARCHABLE_ITEMS;
